import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faArrowRight,
  faCalendarDays,
  faChevronDown,
  faClock,
  faFileContract,
  faReceipt,
  faTicket,
  faTrophy,
} from "@fortawesome/free-solid-svg-icons";
import styles from "./Promociones.module.css";
import { BlogCover } from "../blog/BlogCard";
import useDocumentMeta from "../blog/useDocumentMeta";
import { safeCtaUrl, sanitizeBlogHtml } from "../blog/blogUtils";
import { EmptyState, Spinner } from "../../../components/ui";
import { useAuth } from "../../../context/authContext";
import { raffleService } from "../../../services/api";
import RaffleBuyBox from "./RaffleBuyBox";
import RaffleReceiptModal from "./RaffleReceiptModal";
import { PromotionsComingSoon } from "./PromocionesPage";
import {
  formatPromoDateTime,
  formatPromoDateTimeInline,
  isRaffle,
  promoCoverSource,
  promoMoney,
  promoStatusBadge,
  ticketOutcome,
  uniqueReceiptCodes,
} from "./promoUtils";

function BackLink() {
  return (
    <Link to="/promociones" className={styles.backLink}>
      <FontAwesomeIcon icon={faArrowLeft} /> Volver a promociones
    </Link>
  );
}

/** Fechas clave de la rifa / promoción. */
function DatesList({ promo }) {
  const display = String(promo.display_status || "").toLowerCase();
  const rows = [];
  if (display === "upcoming" && promo.starts_at) {
    rows.push({ icon: faClock, label: "Ventas abren", value: formatPromoDateTime(promo.starts_at) });
  }
  if (promo.ends_at) {
    rows.push({
      icon: faClock,
      label: isRaffle(promo) ? "Cierre de ventas" : "Vigente hasta",
      value: formatPromoDateTime(promo.ends_at),
    });
  }
  if (isRaffle(promo) && promo.draw_at) {
    rows.push({ icon: faCalendarDays, label: "Sorteo", value: formatPromoDateTime(promo.draw_at) });
  }
  if (rows.length === 0) return null;
  return (
    <ul className={styles.datesList}>
      {rows.map((r) => (
        <li key={r.label}>
          <FontAwesomeIcon icon={r.icon} className={styles.datesIcon} />
          <div>
            <span className={styles.datesLabel}>{r.label}</span>
            <span className={styles.datesValue}>{r.value}</span>
          </div>
        </li>
      ))}
    </ul>
  );
}

/** Boletas vendidas / disponibles con barra de progreso. */
function TicketsProgress({ raffle }) {
  const max = Number(raffle.max_tickets) || 0;
  const sold = Number(raffle.tickets_sold) || 0;
  const left = raffle.tickets_left;
  if (left == null && !max) return null;
  const pct = max > 0 ? Math.min(100, Math.round((sold / max) * 100)) : 0;
  return (
    <div className={styles.progressBlock}>
      <div className={styles.progressHead}>
        <span>
          {left != null
            ? Number(left) > 0
              ? `Quedan ${Number(left).toLocaleString("es-CO")} boletas`
              : "Boletas agotadas"
            : "Boletas sin límite"}
        </span>
        {max > 0 && <span className={styles.progressPct}>{pct}% vendido</span>}
      </div>
      {max > 0 && (
        <div
          className={styles.progressTrack}
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={max}
          aria-valuenow={sold}
          aria-label="Boletas vendidas"
        >
          <span className={styles.progressFill} style={{ width: `${pct}%` }} />
        </div>
      )}
    </div>
  );
}

const PromocionDetailPage = () => {
  const { slug } = useParams();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [raffle, setRaffle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(0);
  const [featureDisabled, setFeatureDisabled] = useState(false);
  const [receiptCode, setReceiptCode] = useState(null);

  const applyResult = useCallback((res) => {
    setRaffle(res.ok ? res.data : null);
    setStatus(res.status);
    setFeatureDisabled(res.featureDisabled);
  }, []);

  useEffect(() => {
    if (authLoading) return undefined;
    let cancelled = false;
    setLoading(true);
    raffleService.get(slug).then((res) => {
      if (cancelled) return;
      applyResult(res);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [slug, isAuthenticated, authLoading, applyResult]);

  /** Recarga silenciosa (sin spinner) tras comprar o ante un error de cupo. */
  const refresh = useCallback(() => {
    raffleService.get(slug).then((res) => {
      if (res.ok) applyResult(res);
    });
  }, [slug, applyResult]);

  const descriptionHtml = useMemo(
    () => sanitizeBlogHtml(raffle?.description_html),
    [raffle?.description_html]
  );
  const termsHtml = useMemo(() => sanitizeBlogHtml(raffle?.terms_html), [raffle?.terms_html]);

  useDocumentMeta(
    raffle ? `${raffle.title} | Promociones Gold Loterías` : "",
    raffle?.subtitle || ""
  );

  if (loading || authLoading) {
    return (
      <div className={styles.page}>
        <Spinner label="Cargando promoción…" />
      </div>
    );
  }

  // Con el módulo apagado el detalle de una promoción informativa (PROMO_INFO) sigue llegando.
  if (featureDisabled && !raffle) {
    return (
      <div className={styles.page}>
        <PromotionsComingSoon />
        <div className={styles.centerRow}>
          <Link to="/welcome" className={styles.backLink}>
            <FontAwesomeIcon icon={faArrowLeft} /> Volver al inicio
          </Link>
        </div>
      </div>
    );
  }

  if (!raffle) {
    return (
      <div className={styles.page}>
        <EmptyState
          title={status === 404 ? "No encontramos esta promoción" : "No pudimos cargar la promoción"}
          description={
            status === 404
              ? "Puede que haya sido retirada o que el enlace esté incompleto."
              : "Revisa tu conexión e inténtalo de nuevo."
          }
        />
        <div className={styles.centerRow}>
          <BackLink />
        </div>
      </div>
    );
  }

  const raffleKind = isRaffle(raffle);
  const badge = promoStatusBadge(raffle);
  const sponsor = raffle.sponsor_lottery;
  const myTickets = Array.isArray(raffle.my_tickets) ? raffle.my_tickets : [];
  const myReceipts = uniqueReceiptCodes(myTickets);
  const ctaUrl = safeCtaUrl(raffle.cta_url);
  const CtaTag = ctaUrl.startsWith("/") ? Link : "a";
  const ctaProps = ctaUrl.startsWith("/")
    ? { to: ctaUrl }
    : { href: ctaUrl, target: "_blank", rel: "noopener noreferrer" };
  const termsPageHref = `/terminos-rifa/${encodeURIComponent(raffle.slug)}${
    raffle.terms_version ? `?version=${encodeURIComponent(raffle.terms_version)}` : ""
  }`;

  return (
    <article className={`${styles.page} ${styles.detail}`}>
      <BackLink />

      <BlogCover post={promoCoverSource(raffle)} className={styles.detailBanner} eager />

      <div className={styles.detailGrid}>
        <header className={styles.detailHead}>
          <span className={`${styles.statusPill} ${styles.statusPillInline} ${styles[`pill_${badge.variant}`] || ""}`}>
            {badge.label}
          </span>
          <h1 className={styles.detailTitle}>{raffle.title}</h1>
          {raffle.subtitle && <p className={styles.detailSubtitle}>{raffle.subtitle}</p>}
        </header>

        <aside className={styles.detailAside}>
          <div className={styles.asideCard}>
            {raffleKind && (
              <div className={styles.priceRow}>
                <span className={styles.priceLabel}>Valor de la boleta</span>
                <span className={styles.priceValue}>{promoMoney(raffle.ticket_price)}</span>
              </div>
            )}
            {raffleKind && <TicketsProgress raffle={raffle} />}
            <DatesList promo={raffle} />

            {raffleKind && featureDisabled ? (
              <p className={styles.detailSubtitle}>
                La compra de boletas no está disponible en este momento.
              </p>
            ) : raffleKind ? (
              <RaffleBuyBox raffle={raffle} onRefresh={refresh} onOpenReceipt={setReceiptCode} />
            ) : (
              ctaUrl && (
                <CtaTag className={styles.buyBtn} {...ctaProps}>
                  {raffle.cta_text || "Quiero participar"}
                  <FontAwesomeIcon icon={faArrowRight} />
                </CtaTag>
              )
            )}
          </div>
        </aside>

        <div className={styles.detailBody}>
          {raffle.winner?.number && (
            <div className={styles.winnerBox}>
              <FontAwesomeIcon icon={faTrophy} className={styles.winnerIcon} />
              <div>
                <p className={styles.winnerLabel}>Número ganador</p>
                <p className={styles.winnerNumber}>{raffle.winner.number}</p>
                {raffle.winner.drawn_at && (
                  <p className={styles.winnerDate}>
                    Sorteado el {formatPromoDateTimeInline(raffle.winner.drawn_at)}
                  </p>
                )}
              </div>
            </div>
          )}

          {descriptionHtml && (
            <div
              className={styles.richText}
              // description_html viene saneado del backend y se limpia de nuevo en sanitizeBlogHtml.
              dangerouslySetInnerHTML={{ __html: descriptionHtml }}
            />
          )}

          {raffleKind && (raffle.prize_name || raffle.prize_description || raffle.prize_image) && (
            <section className={styles.infoBlock} aria-labelledby="promo-prize-title">
              <h2 id="promo-prize-title" className={styles.blockTitle}>
                <FontAwesomeIcon icon={faTrophy} /> Premio
              </h2>
              <div className={styles.prizeRow}>
                {raffle.prize_image && (
                  <img
                    src={raffle.prize_image}
                    alt={raffle.prize_name || "Premio"}
                    className={styles.prizeImg}
                    loading="lazy"
                    decoding="async"
                  />
                )}
                <div>
                  {raffle.prize_name && <p className={styles.prizeName}>{raffle.prize_name}</p>}
                  {raffle.prize_description && (
                    <p className={styles.prizeDesc}>{raffle.prize_description}</p>
                  )}
                </div>
              </div>
            </section>
          )}

          {sponsor?.name && (
            <section className={styles.infoBlock} aria-labelledby="promo-sponsor-title">
              <h2 id="promo-sponsor-title" className={styles.blockTitle}>
                Lotería patrocinadora
              </h2>
              <div className={styles.sponsorRow}>
                {sponsor.logo && (
                  <img
                    src={sponsor.logo}
                    alt=""
                    className={styles.sponsorLogo}
                    loading="lazy"
                    decoding="async"
                  />
                )}
                <p className={styles.sponsorName}>{sponsor.name}</p>
              </div>
              {raffle.requires_lottery_purchase && (
                <p className={styles.sponsorRule}>
                  Para participar debes haber comprado un billete de <strong>{sponsor.name}</strong>{" "}
                  en Gold Loterías
                  {raffle.starts_at && raffle.ends_at
                    ? ` entre el ${formatPromoDateTimeInline(raffle.starts_at)} y el ${formatPromoDateTimeInline(raffle.ends_at)}`
                    : " durante la vigencia de la rifa"}
                  .{" "}
                  <Link to="/apuestas" className={styles.inlineLink}>
                    Comprar billete
                  </Link>
                </p>
              )}
            </section>
          )}

          {myTickets.length > 0 && (
            <section className={styles.infoBlock} aria-labelledby="promo-mine-title">
              <h2 id="promo-mine-title" className={styles.blockTitle}>
                <FontAwesomeIcon icon={faTicket} /> Tus boletas
              </h2>
              <ul className={styles.ticketChips}>
                {myTickets.map((t) => {
                  const outcome = ticketOutcome(t, raffle);
                  return (
                    <li
                      key={`${t.number}-${t.receipt_code}`}
                      className={`${styles.ticketChip} ${
                        outcome.key === "won" ? styles.ticketChipWinner : ""
                      } ${outcome.key === "refunded" ? styles.ticketChipMuted : ""}`}
                      title={outcome.label}
                    >
                      {t.number}
                    </li>
                  );
                })}
              </ul>
              {myReceipts.length > 0 && (
                <div className={styles.receiptLinks}>
                  {myReceipts.map((code) => (
                    <button
                      key={code}
                      type="button"
                      className={styles.textBtn}
                      onClick={() => setReceiptCode(code)}
                    >
                      <FontAwesomeIcon icon={faReceipt} /> Comprobante {code}
                    </button>
                  ))}
                </div>
              )}
            </section>
          )}

          {termsHtml && (
            <details className={styles.accordion}>
              <summary className={styles.accordionSummary}>
                <span>
                  <FontAwesomeIcon icon={faFileContract} /> Términos y condiciones
                </span>
                <FontAwesomeIcon icon={faChevronDown} className={styles.accordionChevron} />
              </summary>
              <div className={styles.accordionBody}>
                <div
                  className={styles.richText}
                  // terms_html viene saneado del backend y se limpia de nuevo en sanitizeBlogHtml.
                  dangerouslySetInnerHTML={{ __html: termsHtml }}
                />
                <Link to={termsPageHref} className={styles.inlineLink}>
                  Ver versión imprimible
                  {raffle.terms_version ? ` (versión ${raffle.terms_version})` : ""}
                </Link>
              </div>
            </details>
          )}
        </div>
      </div>

      <RaffleReceiptModal
        isOpen={Boolean(receiptCode)}
        receiptCode={receiptCode}
        raffleTitle={raffle.title}
        onClose={() => setReceiptCode(null)}
      />
    </article>
  );
};

export default PromocionDetailPage;
